import { Tile } from '@components/shared/tile.component';
import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/utils';
import type { FC } from 'react';
import { type MeiaPhaseKey, meiaPhaseSteps } from '../meia.constants';
import { type MeiaPhaseProgressBand, getMeiaPhaseProgressBand } from './meia-phase-progress.util';

export type MeiaPhaseProgressMissionRow = {
  missionCode: string;
  missionId: string;
  missionName: string;
  progressByPhase: Record<MeiaPhaseKey, number>;
};

type MeiaPhaseProgressTileProps = {
  isError?: boolean;
  isLoading?: boolean;
  missionRows: MeiaPhaseProgressMissionRow[];
  studyProgressByPhase: Record<MeiaPhaseKey, number>;
  title?: string;
};

const bandBarClasses: Record<MeiaPhaseProgressBand, string> = {
  empty: 'bg-gray-300',
  green: 'bg-emerald-600',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
};

const ProgressBar: FC<{ className?: string; percentage: number }> = (props) => {
  const { className, percentage } = props;

  return (
    <div className={cn('h-1.5 overflow-hidden rounded-full bg-gray-100', className)}>
      <div
        className={cn('h-full', bandBarClasses[getMeiaPhaseProgressBand(percentage)])}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

export const MeiaPhaseProgressTile: FC<MeiaPhaseProgressTileProps> = (props) => {
  const { isError = false, isLoading = false, missionRows, studyProgressByPhase, title = 'Phase Progress' } = props;
  const isMultiMission = missionRows.length > 1;

  return (
    <Tile
      header={{
        actions: (
          <span className="text-xs text-gray-400">
            {isMultiMission ? `Total across ${missionRows.length} missions` : 'Single mission'}
          </span>
        ),
        title,
      }}
    >
      {isError ? (
        <p className="text-xs text-red-700">Unable to load phase progress. Reload the page to try again.</p>
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <ul className="flex flex-col gap-2.5">
          {meiaPhaseSteps.map((phase) => {
            const percentage = studyProgressByPhase[phase.key];

            return (
              <li
                key={phase.key}
                className="text-xs"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 font-mono text-gray-400">{phase.label}</span>
                  <span className="flex-1 text-gray-800">{phase.name}</span>
                  <ProgressBar
                    className="w-32"
                    percentage={percentage}
                  />
                  <span className="w-10 text-right font-mono text-gray-500">{percentage}%</span>
                </div>
                {isMultiMission && (
                  <div className="ml-10 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {missionRows.map((missionRow) => {
                      const missionPercentage = missionRow.progressByPhase[phase.key];

                      return (
                        <div
                          key={missionRow.missionId}
                          className="flex items-center gap-1 text-[10px] text-gray-400"
                          title={`${missionRow.missionName} · ${missionPercentage}%`}
                        >
                          <span>{missionRow.missionCode}</span>
                          <ProgressBar
                            className="h-1 w-14"
                            percentage={missionPercentage}
                          />
                          <span className="w-7 text-right">{missionPercentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Tile>
  );
};
